import React, { useEffect, useState } from "react";
import styled from "styled-components";
import i18n from "../i18n";

import FlowAddressInput from "./FlowAddressInput";
import { object, func } from "prop-types";
import { fetchStreets, fetchCities } from "../helpers/addressService";
import { useDebouncedCallback } from "use-debounce";
import { SearchSelectInput, TextInput } from "wg-fe-ui/dist";

const FlowAddressContainer = ({
  errors,
  values,
  handleChange,
  defaultValues,
  title,
}) => {
  const [addressValues, setAddressValues] = useState(values);
  const [streetNames, setStreetNames] = useState();
  const [municipalityName, setMunicipalityName] = useState();
  const [selected, setSelected] = useState({});
  const [postals, setPostals] = useState({});

  useEffect(() => {
    setAddressValues(defaultValues);
  }, [defaultValues]);

  console.log("Address default values:", defaultValues);

  useEffect(() => {
    //called once when page loads
    //not called again until given in address AND number, and hitting enter => called when all values are filled in
    //called twice in total: when page loads, and when the address is given in and the API call is done to fill in the remaining inputs

    if (addressValues) {
      const selectedObj = {};
      Object.keys(addressValues).forEach((key) => {
        handleChange({ name: key, value: addressValues[key] });
        const selectedItem = {
          label: addressValues[key],
          value: addressValues[key],
        };
        selectedObj[key] = selectedItem;
      });
      setSelected(selectedObj);
    }
  }, [addressValues]);

  useEffect(() => {
    if (values.streetName) {
      getStreetNames({
        searchQuery: values["streetName"].substring(0, 6),
        municipalityName: values["municipalityName"],
      });
    }
  }, [values.streetName]);

  const getStreetNames = async (val, callback) => {
    const resp = await fetchStreets({ searchQuery: val }, values);
    setStreetNames(resp);
    if (callback) {
      callback(resp);
    } else {
      return resp;
    }
  };

  const getCityNames = async (val, callback) => {
    if (callback) {
      const resp = await fetchCities({ searchQuery: val });
      setStreetNames({});
      setMunicipalityName(resp.citiesOptionList);
      // delete streetname
      const tmpSelected = { ...selected };
      delete tmpSelected["streetName"];
      setSelected(tmpSelected);
      handleChange({ name: "streetName", value: undefined });
      setPostals(resp.cityPostals);

      callback(resp.citiesOptionList);
    } else {
      const resp = await fetchCities(val);
      setStreetNames({});
      setMunicipalityName(resp.citiesOptionList);
      // delete streetname
      const tmpSelected = { ...selected };
      delete tmpSelected["streetName"];
      setSelected(tmpSelected);
      handleChange({ name: "streetName", value: undefined });
      setPostals(resp.cityPostals);
      return resp.citiesOptionList;
    }
  };

  const handleSelected = (value) => {
    const selectedVal = { label: value.value, value: value.value };
    const tmpSelected = { ...selected };
    if (value.name === "municipalityName") {
      //delete streetname
      delete tmpSelected["streetName"];
      if (postals[selectedVal.value]) {
        tmpSelected["postalCode"] = postals[selectedVal.value];
        handleChange({ name: "postalCode", value: postals[selectedVal.value] });
      }
    }
    tmpSelected[value.name] = selectedVal;
    setSelected(tmpSelected);
    handleChange(value);
  };

  const handleZipcodeValidation = async () => {
    if (values["postalCode"] && values["postalCode"].length === 4) {
      const [resp] = await getCityNames({
        zipcode: parseInt(values["postalCode"]),
      });

      handleChange({ name: "streetName", value: undefined });
      handleChange({ name: "streetNumber", value: undefined });
      handleSelected({ name: "municipalityName", value: resp.value });
    }
  };

  const [debounceFetchCities] = useDebouncedCallback(getCityNames, 500);
  const [debounceFetchStreets] = useDebouncedCallback(getStreetNames, 500);

  return (
    <div>
      {Object.keys(selected).length > 0 ? (
        <>
          <FlexWrapper>
            <SearchSelectInput
              async
              cacheOptions={false}
              defaultOptions={municipalityName}
              error={errors.municipalityName}
              id="municipalityName"
              name="municipalityName"
              loadOptions={debounceFetchCities}
              onSelected={(value) => handleSelected(value)}
              value={selected.municipalityName || ""}
              noOptionsMessage={() => "no city found"}
            >
              {i18n.t("Municipality")} *
            </SearchSelectInput>
            <TextInput
              error={errors.postalCode}
              onChange={handleChange}
              type="number"
              name="postalCode"
              dataTestId="underwriting_address_postalCode"
              value={values["postalCode"]}
              onBlur={() => handleZipcodeValidation()}
              placeholder="zipcode"
            >
              {i18n.t("Postal code")} *
            </TextInput>
          </FlexWrapper>
          <FlexWrapper>
            <SearchSelectInput
              async
              cacheOptions={false}
              defaultOptions={streetNames}
              error={errors.streetNames}
              id="streetName"
              name="streetName"
              loadOptions={debounceFetchStreets}
              onSelected={(value) => handleSelected(value)}
              value={selected.streetName || ""}
              noOptionsMessage={() => "no streets found"}
            >
              {i18n.t("Street")} *
            </SearchSelectInput>
            <FlexWrapper>
              <TextInput
                error={errors.streetNumber}
                onChange={handleChange}
                type="text"
                name="streetNumber"
                disabled={!values["streetName"]}
                value={
                  values["streetNumber"] && values["streetName"]
                    ? values["streetNumber"]
                    : ""
                }
              >
                <FlowAdressInputLabel>
                  {i18n.t("Housenr")} *
                </FlowAdressInputLabel>
              </TextInput>
              <TextInput
                error={errors.boxNumber}
                onChange={handleChange}
                type="string"
                name="boxNumber"
                disabled={!values["streetName"]}
                value={values["boxNumber"] ? values["boxNumber"] : ""}
              >
                {i18n.t("Boxnr")}
              </TextInput>
            </FlexWrapper>
          </FlexWrapper>
        </>
      ) : (
        <FlowAddressInputWrapper>
          <FlowAddressInput
            fullAddressError={errors.municipalityName}
            addressValues={(val) => setAddressValues(val)}
            title={title}
          />
        </FlowAddressInputWrapper>
      )}
    </div>
  );
};

const FlowAdressInputLabel = styled.p`
  height: 1rem;
`;

const FlowAddressInputWrapper = styled.div`
  height: 9rem;
`;

const FlexWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  & > div + div {
    margin-left: 1rem;
  }
  @media (max-width: 400px) {
    flex-direction: column;
    height: unset;
    & > div + div {
      margin-left: 0;
    }
  }
`;

FlowAddressContainer.propTypes = {
  errors: object.isRequired,
  values: object.isRequired,
  handleChange: func.isRequired,
  title: object,
};

export default FlowAddressContainer;
